import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import React, { useCallback, useMemo, useRef } from 'react';

import { PartTableRow } from './PartTableRow';
import { SerialCopyHeaderButton } from './SerialCopyHeaderButton';
import { CustomPermission } from '../../../@types';
import { elasticLastColumnSx, stickyActionsTableSx } from '../../../configs/tableStyles';
import { useFreezeColumnWidths } from '../../../hooks/useFreezeColumnWidths';
import { ColumnDef, SortModel } from '../ServiceTable';
import { ServiceTableHead } from '../ServiceTable/ServiceTableHead';

// Fixed widths of the non-data columns, used to size the table when frozen.
const CHECKBOX_COL_WIDTH = 48;
const ACTIONS_COL_WIDTH = 70;

export interface PartTableProps {
  data: Record<string, unknown>[];
  columns: ColumnDef[];
  sortModel: SortModel;
  onSortChange: (model: SortModel) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onColumnReorder?: (oldIndex: number, newIndex: number) => void;
  onColumnResize?: (key: string, width: number, seed?: Record<string, number>) => void;
  loading?: boolean;
  permissions?: CustomPermission[];
  /** When true, the selection column (checkboxes + Select-all) and the
   *  per-row action column (Create Repair Job) are not rendered. Mirrors the
   *  ServiceTable client/customer treatment so client users see a read-only
   *  list without actions they couldn't perform anyway. */
  isClientCustomer?: boolean;
}

const DEFAULT_SORT_FIELD = 'assetId';

const collectSerials = (data: Record<string, unknown>[]): string[] =>
  data.map((r) => r.serial1).filter((v): v is string => typeof v === 'string' && v.length > 0);

const withSerialCopyAdornment = (cols: ColumnDef[], serials: string[]): ColumnDef[] =>
  cols.map((c) =>
    c.key === 'serial1'
      ? { ...c, headerEndAdornment: <SerialCopyHeaderButton serials={serials} /> }
      : c
  );

export const PartTable: React.FC<PartTableProps> = ({
  data,
  columns,
  sortModel,
  onSortChange,
  selectedIds,
  onSelectionChange,
  onColumnReorder,
  onColumnResize,
  loading,
  permissions,
  isClientCustomer = false,
}) => {
  const allIds = useMemo(
    () => data.map((r) => (r.id as number | undefined) ?? 0).filter(Boolean),
    [data]
  );
  const pageSerials = useMemo(() => collectSerials(data), [data]);
  const visibleColumns = useMemo(
    () =>
      withSerialCopyAdornment(
        columns.filter((c) => c.visible),
        pageSerials
      ),
    [columns, pageSerials]
  );
  // When every visible column has an explicit width the table renders as a
  // fixed-pixel colgroup, so resizing one column never disturbs the others and
  // the table scrolls horizontally instead of squeezing.
  const isFrozen = visibleColumns.length > 0 && visibleColumns.every((c) => c.width != null);
  const frozenTableWidth = useMemo(() => {
    if (!isFrozen) return undefined;
    const dataWidth = visibleColumns.reduce((sum, c) => sum + (c.width ?? 0), 0);
    return dataWidth + (isClientCustomer ? 0 : CHECKBOX_COL_WIDTH + ACTIONS_COL_WIDTH);
  }, [isFrozen, visibleColumns, isClientCustomer]);

  // Seed every not-yet-sized column to its rendered content width immediately
  // after the first paint, so the table is ALWAYS in the precise fixed-pixel
  // model. Auto-layout can't shrink a column below its content, which is what
  // made dragging drift and columns un-shrinkable.
  const tableRef = useRef<HTMLTableElement>(null);
  useFreezeColumnWidths({ tableRef, isFrozen, ready: data.length > 0, onColumnResize });

  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = allIds.some((id) => selectedIds.includes(id));

  const handleSort = useCallback(
    (field: string) => {
      if (sortModel.field !== field) {
        onSortChange({ field, direction: 'asc' });
      } else if (sortModel.direction === 'asc') {
        onSortChange({ field, direction: 'desc' });
      } else {
        onSortChange({ field: DEFAULT_SORT_FIELD, direction: 'desc' });
      }
    },
    [sortModel, onSortChange]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      onSelectionChange(checked ? allIds : []);
    },
    [allIds, onSelectionChange]
  );

  const toggleSelection = useCallback(
    (id: number) => {
      const next = selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id];
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange]
  );

  const handleShiftClick = useCallback(
    (index: number) => {
      const lastSelectedIdx = data.findIndex((r) => r.id === selectedIds[selectedIds.length - 1]);
      if (lastSelectedIdx === -1) {
        const id = data[index]?.id as number | undefined;
        if (id) onSelectionChange([...selectedIds, id]);
        return;
      }
      const start = Math.min(lastSelectedIdx, index);
      const end = Math.max(lastSelectedIdx, index);
      const rangeIds = data
        .slice(start, end + 1)
        .map((r) => (r.id as number | undefined) ?? 0)
        .filter(Boolean);
      const merged = Array.from(new Set([...selectedIds, ...rangeIds]));
      onSelectionChange(merged);
    },
    [data, selectedIds, onSelectionChange]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
        </Box>
      )}
      <TableContainer>
        <Table
          ref={tableRef}
          size="small"
          aria-label="Parts table"
          data-testid="part-table"
          sx={
            isFrozen
              ? {
                  tableLayout: 'fixed',
                  width: frozenTableWidth,
                  // The frozen width is the sum of the measured column widths,
                  // which is routinely narrower than the container — that gap is
                  // the white band beside the sticky actions column. Floor the
                  // table at the container width; the elastic last data column in
                  // the colgroup below absorbs the slack, so every other column
                  // keeps the exact width it was frozen at.
                  minWidth: '100%',
                  ...stickyActionsTableSx,
                  ...elasticLastColumnSx,
                }
              : // Size columns to their content (fit the longest cell) rather
                // than stretching to fill — the exact widths the first resize
                // freezes to (so nothing shifts on first grab). Wider-than-
                // viewport tables scroll horizontally.
                //
                // `minWidth: 100%` keeps a narrow table from ending short of its
                // container, which otherwise leaves a white band beside the
                // sticky actions column. See elasticLastColumnSx.
                {
                  width: 'max-content',
                  minWidth: '100%',
                  ...stickyActionsTableSx,
                  ...elasticLastColumnSx,
                }
          }
        >
          {isFrozen && (
            <colgroup>
              {!isClientCustomer && <col style={{ width: CHECKBOX_COL_WIDTH }} />}
              {visibleColumns.map((c, i) => (
                <col
                  key={c.key}
                  // Last data column is left elastic (no width) so it absorbs any
                  // slack when the frozen width is under the container width.
                  // Under `table-layout: fixed` a width-less column takes the
                  // remainder; giving every column a width would instead stretch
                  // them all proportionally and undo the user's dragged widths.
                  // Trade-off: this last column is no longer independently
                  // resizable — already the intent of elasticLastColumnSx.
                  style={i === visibleColumns.length - 1 ? undefined : { width: c.width }}
                />
              ))}
              {!isClientCustomer && <col style={{ width: ACTIONS_COL_WIDTH }} />}
            </colgroup>
          )}
          <ServiceTableHead
            columns={visibleColumns}
            sortModel={sortModel}
            onSort={handleSort}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            // The last data column is elastic while frozen, so it has no width
            // of its own to drag — suppress its resize handle.
            elasticLastColumn={isFrozen}
            isClientCustomer={isClientCustomer}
          />
          <TableBody>
            {data.map((row, idx) => {
              const id = (row.id as number | undefined) ?? 0;
              return (
                <PartTableRow
                  key={id || idx}
                  row={row}
                  columns={visibleColumns}
                  selected={selectedIds.includes(id)}
                  even={idx % 2 === 1}
                  permissions={permissions}
                  isClientCustomer={isClientCustomer}
                  onClick={() => toggleSelection(id)}
                  onShiftClick={() => handleShiftClick(idx)}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
